import { useInterpret } from '@xstate/react';
import { omit } from 'lodash';
import * as React from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { InterpreterFrom } from 'xstate';

import {
  Address,
  AddressFormFeatures,
  AddressFormLayoutField,
  AddressFormValidation,
  Recipient,
} from 'api/api';

import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';

import {
  AutocompleteMachine,
  createAutocompleteMachine,
} from '~address-form/inputs/autocomplete/machine';
import {
  RecipientAddressMachine,
  createAddressMachine,
} from '~address-form/state-machines/address-machine';

import { Entries, MetaAddressFormFields } from '../types';

export const AddressFormBusinessLogicContext = React.createContext({
  autocompleteService: {} as InterpreterFrom<AutocompleteMachine>,
  addressService: {} as InterpreterFrom<RecipientAddressMachine>,
  formInstance: {} as UseFormReturn<MetaAddressFormFields>,
  initialAddress: {} as Recipient | undefined,
  cleanForm: () => undefined as void,
  isFormClear: () => false as boolean,
  submitHandler: (() => null) as Props['onSubmit'],
  addressFormLayout: [] as AddressFormLayoutField[],
  addressFormValidationConfig: {} as AddressFormValidation,
  addressFormFeatures: {} as AddressFormFeatures,
  addressPrefill: {} as Recipient | undefined,
  isModalForm: {} as boolean,
  extendedAddress: {} as Address | undefined,
  setExtendedAddress: {} as React.Dispatch<React.SetStateAction<Address | undefined>>,
});

interface Props {
  onSubmit: (address: Recipient, onError?: () => void) => void;
  address: Recipient | undefined;
  // When opening an already saved, validated address, we don't want to validate again unless user changes something significant
  // Prop below allows to specify whether address prop is a "edit existing address" or "prefill partial, unverified address" case
  addressVerified?: boolean;
  isBillingAddress?: boolean;
  isModalForm: boolean;
  extendedAddress?: Address;
  setExtendedAddress: React.Dispatch<React.SetStateAction<Address | undefined>>;
}

export const AddressFormBusinessProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  onSubmit,
  address,
  addressVerified = true,
  isBillingAddress,
  isModalForm,
  extendedAddress,
  setExtendedAddress,
}) => {
  const state = useGlobalState();
  const { locale, tICU } = useTranslations();

  const formInstance = useForm<MetaAddressFormFields>({
    defaultValues: {
      first_name: '',
      last_name: '',
      address_lines: [''],
      street: '',
      street_number: '',
      apartment_number: '',
      postal_code: '',
      phone_number: '',
      phone_number_country: '',
      vat: '',
      vat_type: '',
      subregion: '',
      region: '',
      customs_number: '',
      country: state.purchase_country,
      ...address,
    },
    criteriaMode: 'firstError',
    mode: 'onSubmit',
    shouldUnregister: false,
  });

  React.useEffect(() => {
    if (
      state.address_form_configuration?.global_features?.enable_extended_validation &&
      extendedAddress
    ) {
      for (const [key, value] of Object.entries(extendedAddress) as Entries<
        Required<MetaAddressFormFields>
      >) {
        // TODO check if there could be val: undefined in the response
        if (value) formInstance.setValue(key, value);
      }

      formInstance.setError('address_lines.0', {
        type: 'warning',
        message: tICU('widget.validation.MISSING_STREET_NUMBER'),
      });
      formInstance.clearErrors('region');
    }
  }, [extendedAddress]);

  const inputValue = formInstance.getValues('address_lines.0');

  const prepareAddressFormConfig = () => {
    const isBillingAddressFormLayoutAvailable =
      isBillingAddress && !!state.address_form_configuration?.billing_layout;
    return {
      addressFormLayout: isBillingAddressFormLayoutAvailable
        ? state.address_form_configuration?.billing_layout?.fields ?? []
        : state.address_form_configuration?.layout.fields ?? [],
      addressFormValidation: isBillingAddress
        ? state.address_form_configuration?.billing_validation ?? {}
        : state.address_form_configuration?.validation ?? {},
      addressFormFeatures: isBillingAddress
        ? state.address_form_configuration?.billing_features ?? {}
        : state.address_form_configuration?.features ?? {},
      addressPrefill: isBillingAddress ? state.prefill_billing : state.prefill_recipient,
    };
  };

  const { addressFormLayout, addressFormValidation, addressFormFeatures, addressPrefill } =
    prepareAddressFormConfig();

  const [autocompleteMachine] = React.useState(() =>
    createAutocompleteMachine({
      country: state.purchase_country,
      locale,
      checkout_session_id: state.checkout_session_id,
      inputValue,
    })
  );
  const autocompleteService = useInterpret(autocompleteMachine);

  const [addressMachine] = React.useState(() =>
    createAddressMachine({
      country: state.purchase_country,
      initialAddress: address,
      addressVerified,
      provider: addressFormFeatures.suggestion_provider,
      extendedValidation:
        !!state.address_form_configuration?.global_features?.enable_extended_validation,
    })
  );
  const addressService = useInterpret(addressMachine, {
    actions: {
      submitForm: () => {
        onSubmit(formInstance.getValues(), () => {
          addressService.send('ADDRESS_UPDATE_FAIL');
        });
      },
      updateFormValuesWithSuggestionStreetDetails: (_, event) => {
        formInstance.reset({
          ...formInstance.getValues(),
          street: event.payload.street,
          street_number: event.payload.street_number,
        });
      },
    },
  });

  React.useState(() => autocompleteService.start());
  React.useState(() => addressService.start());

  const cleanForm = () => {
    autocompleteService.send('CLEAR_EVERYTHING');

    const clearedValues = Object.entries(formInstance.getValues()).reduce(
      (result, [name, value]) => {
        if (name === 'address_lines') {
          return { ...result, [name]: [''] };
        }
        if (name === 'id') {
          return { ...result, [name]: value };
        }
        return { ...result, [name]: '' };
      },
      {}
    );

    formInstance.reset(clearedValues);
  };

  const isFormClear = () => {
    return !Object.values(omit(formInstance.getValues(), ['id'])).some(value => {
      if (Array.isArray(value)) {
        // address_lines case
        return !!value[0];
      }
      return !!value;
    });
  };

  return (
    <AddressFormBusinessLogicContext.Provider
      value={{
        autocompleteService,
        formInstance,
        addressService,
        initialAddress: address,
        cleanForm,
        isFormClear,
        submitHandler: onSubmit,
        addressFormLayout: addressFormLayout,
        addressFormValidationConfig: addressFormValidation,
        addressFormFeatures,
        addressPrefill,
        isModalForm,
        extendedAddress,
        setExtendedAddress,
      }}
    >
      {children}
    </AddressFormBusinessLogicContext.Provider>
  );
};

export const useAddressFormBusinessLogic = () => React.useContext(AddressFormBusinessLogicContext);

AddressFormBusinessProvider.displayName = 'AddressFormBusinessProvider';
